// router/routes.js
import Layout from '@/layout/index'

// 静态路由（不需要权限，所有用户可见）
export const constantRoutes = [
  {
    path: '/login',
    component: () => import('@/views/login/index'),
    hidden: true
  },
  {
    path: '/',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '',
        name: 'DashboardRedirect',
        component: () => import('@/views/dashboard/index'),
        meta: {
          title: '仪表盘',
          icon: 'el-icon-s-data',
          roles: [0, 1]  // 所有角色都能访问
        }
      }
    ]
  },
  // 404 页面组件定义（不直接添加到路由表）
  {
    path: '/404',
    component: () => import('@/views/error-page/404'),
    hidden: true
  }
]

// 404 路由（单独定义，在动态路由之后添加）
export const notFoundRoute = {
  path: '*',
  redirect: '/404',
  hidden: true
}

// 组件映射表 - 使用更可靠的方式
export const componentMap = {
  // Layout 组件
  'Layout': Layout,
  'layout/index': Layout,
  '@/layout/index': Layout,
  
  // 视图组件
  'Dashboard': () => import('@/views/dashboard/index'),
  'UserList': () => import('@/views/user/list'),
  'GroupList': () => import('@/views/group/list'),
  'BillList': () => import('@/views/bill/list'),
  'BotList': () => import('@/views/bot/list'),
  'AdminList': () => import('@/views/admin/list'),
  
  // 备用：路径映射
  '@/views/dashboard/index': () => import('@/views/dashboard/index'),
  '@/views/user/list': () => import('@/views/user/list'),
  '@/views/group/list': () => import('@/views/group/list'),
  '@/views/bill/list': () => import('@/views/bill/list'),
  '@/views/admin/list': () => import('@/views/admin/list'),
  '@/views/bot/list': () => import('@/views/bot/list'),
  '@/views/error-page/404': () => import('@/views/error-page/404'),
}

// 动态路由结构 - 优化为更标准的格式
export const defaultAsyncRoutes = [
  {
    path: '/user',
    component: 'Layout',  // 改为 'Layout' 而不是 'layout/index'
    redirect: '/user/list',
    name: 'User',
    meta: {
      title: '用户管理',
      icon: 'el-icon-user',
      roles: [0,1],
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        name: 'UserList',  // 确保 name 与组件映射一致
        component: 'UserList',  // 改为组件名而不是路径
        meta: {
          title: '用户列表',
          roles: [0,1]
        }
      }
    ]
  },
  {
    path: '/group',
    component: 'Layout',
    redirect: '/group/list',
    name: 'Group',
    meta: {
      title: '群组管理',
      icon: 'el-icon-s-tools',
      roles: [0, 1],
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        name: 'GroupList',
        component: 'GroupList',
        meta: {
          title: '群组列表',
          roles: [0, 1]
        }
      }
    ]
  },
  {
    path: '/bill',
    component: 'Layout',
    redirect: '/bill/list',
    name: 'Bill',
    meta: {
      title: '账单管理',
      icon: 'el-icon-s-finance',
      roles: [0, 1],
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        name: 'BillList',
        component: 'BillList',
        meta: {
          title: '账单列表',
          roles: [0, 1]
        }
      }
    ]
  },
    

  {
    path: '/bot',
    component: 'Layout',
    redirect: '/bot/list',
    name: 'Bot',
    meta: {
      title: '机器人管理',
      icon: 'el-icon-s-platform',
      roles: [0],
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        name: 'BotList',
        component: 'BotList',
        meta: {
          title: '机器人列表',
          roles: [0, 1]
        }
      }
    ]
  },
  {
    path: '/admin',
    component: 'Layout',
    redirect: '/admin/list',
    name: 'Admin',
    meta: {
      title: '管理员管理',
      icon: 'el-icon-s-finance',
      roles: [0],
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        name: 'AdminList',
        component: 'AdminList',
        meta: {
          title: '管理员列表',
          roles: [0]
        }
      }
    ]
  }
]

// 辅助函数：标准化路由配置
export function normalizeRoutes(routes) {
  return routes.map(route => {
    const normalized = { ...route }
    
    // 标准化 component
    if (normalized.component === 'Layout' || normalized.component === 'layout/index') {
      normalized.component = Layout
    }
    
    // 处理子路由
    if (normalized.children && normalized.children.length > 0) {
      normalized.children = normalizeRoutes(normalized.children)
    }
    
    return normalized
  })
}

// 获取所有路由路径（用于权限检查）
export function getAllRoutePaths(routes) {
  const paths = []
  
  function collectPaths(routeList, parentPath = '') {
    routeList.forEach(route => {
      // 排除特殊路径
      if (route.path && route.path !== '*' && !route.path.includes('404')) {
        const fullPath = parentPath 
          ? `${parentPath}/${route.path}`.replace(/\/\//g, '/')
          : route.path
        paths.push(fullPath)
      }
      
      if (route.children) {
        collectPaths(route.children, route.path)
      }
    })
  }
  
  collectPaths(routes)
  return [...new Set(paths)]
}

// 检查路由是否存在
export function routeExists(path, routes) {
  const allPaths = getAllRoutePaths(routes)
  return allPaths.some(routePath => routePath === path || routePath === `/${path}`)
}

// 路由工具函数
export const routeUtils = {
  isExternal,
  resolvePath
}

// 从其他文件导入的工具函数
function isExternal(path) {
  return /^(https?:|mailto:|tel:)/.test(path)
}

function resolvePath(basePath, routePath) {
  if (isExternal(routePath)) return routePath
  if (isExternal(basePath)) return basePath
  
  if (basePath && !routePath.startsWith('/')) {
    const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
    const cleanRoute = routePath.startsWith('/') ? routePath.slice(1) : routePath
    return `${cleanBase}/${cleanRoute}`
  }
  
  return routePath
}
